import { useState, useRef, useCallback, useEffect } from 'react';
import { WebSocketClient, createWebSocketClient } from '@/lib/websocket';
import { 
  ConnectionStatus, 
  TranslationMessage, 
  TranscriptionMessage,
  ErrorMessage,
  ParticipantUpdateMessage,
  StatusMessage,
  SupportedLanguage
} from '@speech-translator/shared';

export interface ListenerWebSocketState {
  connectionStatus: ConnectionStatus;
  participantCount: number;
  error: string | null;
  isConnected: boolean;
}

export interface ListenerWebSocketCallbacks {
  onTranslation?: (message: TranslationMessage) => void;
  onTranscription?: (message: TranscriptionMessage) => void;
  onError?: (message: ErrorMessage) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  onParticipantUpdate?: (message: ParticipantUpdateMessage) => void;
}

export function useListenerWebSocket(callbacks: ListenerWebSocketCallbacks = {}) {
  const [state, setState] = useState<ListenerWebSocketState>({
    connectionStatus: 'disconnected',
    participantCount: 0,
    error: null,
    isConnected: false
  });

  const wsClientRef = useRef<WebSocketClient | null>(null);
  const currentRoomIdRef = useRef<string | null>(null);
  const currentLanguageRef = useRef<SupportedLanguage>('en');

  // Update connection status
  const updateConnectionStatus = useCallback((status: ConnectionStatus) => {
    setState(prev => ({ 
      ...prev, 
      connectionStatus: status,
      isConnected: status === 'connected'
    }));
    callbacks.onStatusChange?.(status);
  }, [callbacks]);

  // Handle translation messages
  const handleTranslation = useCallback((message: TranslationMessage) => {
    callbacks.onTranslation?.(message);
  }, [callbacks]);

  // Handle transcription messages (for fallback display)
  const handleTranscription = useCallback((message: TranscriptionMessage) => {
    callbacks.onTranscription?.(message);
  }, [callbacks]);

  // Handle error messages
  const handleError = useCallback((message: ErrorMessage) => {
    setState(prev => ({ ...prev, error: message.data.message }));
    callbacks.onError?.(message);
  }, [callbacks]);

  // Handle status messages
  const handleStatus = useCallback((message: StatusMessage) => {
    setState(prev => ({ 
      ...prev, 
      participantCount: message.data.participantCount || 0 
    }));
  }, []);

  // Handle participant updates
  const handleParticipantUpdate = useCallback((message: ParticipantUpdateMessage) => {
    setState(prev => ({ 
      ...prev, 
      participantCount: message.data.participantCount 
    }));
    callbacks.onParticipantUpdate?.(message);
  }, [callbacks]);

  // Connect to a room
  const connect = useCallback(async (roomId: string, language: SupportedLanguage = 'en'): Promise<void> => {
    try {
      // Disconnect existing connection
      if (wsClientRef.current) {
        wsClientRef.current.disconnect();
      }

      // Clear any previous errors
      setState(prev => ({ ...prev, error: null }));

      // Create new WebSocket client
      const client = createWebSocketClient(roomId, 'listener', language);
      wsClientRef.current = client;
      currentRoomIdRef.current = roomId;
      currentLanguageRef.current = language;

      // Set up message handlers
      client.onMessage('translation', handleTranslation);
      client.onMessage('transcription', handleTranscription);
      client.onMessage('error', handleError);
      client.onMessage('status', handleStatus);
      client.onMessage('participant_update', handleParticipantUpdate);

      // Set up status change handler
      client.onStatusChange(updateConnectionStatus);

      // Connect to WebSocket
      await client.connect();

      // Send join room message
      client.send({
        type: 'join_room',
        timestamp: Date.now(),
        data: {
          roomId,
          role: 'listener',
          language
        }
      });

    } catch (error) {
      console.error('Failed to connect to room:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to room';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, [handleTranslation, handleTranscription, handleError, handleStatus, handleParticipantUpdate, updateConnectionStatus]);

  // Change language (reconnect with new language)
  const changeLanguage = useCallback(async (newLanguage: SupportedLanguage): Promise<void> => {
    if (currentRoomIdRef.current && newLanguage !== currentLanguageRef.current) {
      await connect(currentRoomIdRef.current, newLanguage);
    }
  }, [connect]);

  // Disconnect from room
  const disconnect = useCallback(() => {
    if (wsClientRef.current) {
      wsClientRef.current.disconnect();
      wsClientRef.current = null;
    }
    currentRoomIdRef.current = null;
    setState(prev => ({ 
      ...prev, 
      connectionStatus: 'disconnected',
      isConnected: false,
      participantCount: 0,
      error: null
    }));
  }, []);

  // Reconnect to current room
  const reconnect = useCallback(async () => {
    if (currentRoomIdRef.current) {
      await connect(currentRoomIdRef.current, currentLanguageRef.current);
    }
  }, [connect]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Get current room ID
  const getCurrentRoomId = useCallback(() => {
    return currentRoomIdRef.current;
  }, []);

  // Get current language
  const getCurrentLanguage = useCallback(() => {
    return currentLanguageRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    // State
    ...state,
    
    // Actions
    connect,
    disconnect,
    reconnect,
    changeLanguage,
    clearError,
    
    // Getters
    getCurrentRoomId,
    getCurrentLanguage
  };
}